if (!customElements.get('faqs-page')) {
  customElements.define(
    'faqs-page',
    class FaqsPage extends HTMLElement {
      constructor() {
        super();

        this.chaptersList = this.querySelector('.faqs-page-section__chapters-list');
        this.accordionWrapper = this.querySelector('.faqs-page-section__accordion-wrapper');

        this.chapters = [];
        this.chapterGroups = new Map();
        this.chapterNavItems = new Map();
        this.isScrolling = false;
        this.activeChapter = null;
        this.scrollHandler = null;
      }

      connectedCallback() {
        this.extractUniqueChapters();
        this.renderChaptersList();
        this.organizeAccordionsByChapter();
        this.openFirstAccordionPerChapter();
        this.headerOffset = this.getHeaderOffset();
        this.setupScrollSpy();
      }

      getHeaderOffset() {
        const stickyHeader =
          document.querySelector('sticky-header') ||
          document.querySelector('.header-wrapper') ||
          document.querySelector('header');

        if (stickyHeader) {
          return stickyHeader.getBoundingClientRect().height + 20;
        }

        return 20;
      }

      extractUniqueChapters() {
        const seen = new Map();

        this.querySelectorAll('.faqs-accordion-item').forEach((item) => {
          const raw = item.dataset.chapter?.trim();
          if (!raw) return;

          const key = raw.toLowerCase();
          if (!seen.has(key)) seen.set(key, raw);
          item.dataset.chapter = key;
        });

        this.chapters = Array.from(seen, ([value, display]) => ({ value, display }));
      }

      renderChaptersList() {
        if (!this.chaptersList || !this.chapters.length) return;

        this.chaptersList.innerHTML = '';

        this.chapters.forEach((chapter, i) => {
          const li = document.createElement('li');
          li.className = 'faqs-page-section__chapter-item';
          li.textContent = chapter.display;
          li.dataset.chapter = chapter.value;

          if (i === 0) li.classList.add('active');

          li.addEventListener('click', () => this.scrollToChapter(chapter.value));

          this.chaptersList.appendChild(li);
          this.chapterNavItems.set(chapter.value, li);
        });
      }

      organizeAccordionsByChapter() {
        if (!this.accordionWrapper) return;

        const items = Array.from(this.accordionWrapper.querySelectorAll('.faqs-accordion-item'));
        this.accordionWrapper.innerHTML = '';

        this.chapters.forEach((chapter) => {
          const group = document.createElement('div');
          group.className = 'faqs-page-section__chapter-group';
          group.dataset.chapter = chapter.value;

          const heading = document.createElement('div');
          heading.className = 'faqs-page-section__accordion-chapter-name';
          heading.textContent = chapter.display;
          group.appendChild(heading);

          items.forEach((item) => {
            if (item.dataset.chapter === chapter.value) {
              const accordion = item.querySelector('accordion-item');
              if (accordion) group.appendChild(accordion);
            }
          });

          this.accordionWrapper.appendChild(group);
          this.chapterGroups.set(chapter.value, group);
        });
      }

      openFirstAccordionPerChapter() {
        this.chapterGroups.forEach((group) => {
          const accordions = group.querySelectorAll('accordion-item');

          accordions.forEach((item, index) => {
            if (index === 0) {
              if (typeof item.open === 'function') item.open();
            } else if (typeof item.close === 'function') {
              item.close();
            }
          });
        });
      }

      setupScrollSpy() {
        let ticking = false;

        this.scrollHandler = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            // Check isScrolling INSIDE rAF — prevents stale pending frames from overriding click
            if (!this.isScrolling) {
              this.updateActiveFromScroll();
            }
          });
        };

        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }

      updateActiveFromScroll() {
        if (this.isScrolling) return;

        // Mobile/tablet: after a click, keep that active state locked — don't override
        if (this.clickLocked && window.innerWidth < 1024) return;

        const triggerPoint = this.headerOffset + 40;
        let active = null;

        // DOM order — last section whose top has passed the trigger line wins
        this.chapterGroups.forEach((el, name) => {
          if (el.getBoundingClientRect().top <= triggerPoint) {
            active = name;
          }
        });

        // At the very top before any section reaches trigger — default to first
        if (!active && this.chapters.length > 0) {
          active = this.chapters[0].value;
        }

        if (active) this.setActiveChapter(active);
      }

      setActiveChapter(name) {
        if (this.activeChapter === name) return;
        this.activeChapter = name;

        this.chapterNavItems.forEach((li, key) => {
          li.classList.toggle('active', key === name);
        });
      }

      scrollToChapter(name) {
        const target = this.chapterGroups.get(name);
        if (!target) return;

        // Lock active state to clicked chapter
        this.setActiveChapter(name);
        this.clickLocked = true;
        this.isScrolling = true;

        const targetTop = target.getBoundingClientRect().top + window.scrollY - this.headerOffset;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });

        // Detect scroll end by monitoring when scrollY position stabilizes
        // Works in ALL browsers — no scrollend/setTimeout dependency
        let lastY = -1;
        let stableFrames = 0;

        const safetyTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 3000);

        const checkEnd = () => {
          const currentY = Math.round(window.scrollY);

          if (currentY === lastY) {
            stableFrames++;
            // Position unchanged for 5 consecutive frames = scroll finished
            if (stableFrames >= 5) {
              this.isScrolling = false;
              clearTimeout(safetyTimeout);
              return;
            }
          } else {
            stableFrames = 0;
          }

          lastY = currentY;
          requestAnimationFrame(checkEnd);
        };

        requestAnimationFrame(checkEnd);
      }

      disconnectedCallback() {
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }
      }
    },
  );
}
